import React from "react";

import {
  List,
  DataTable,
  TextField,
  BooleanField,
  TextInput,
  NumberInput,
  BooleanInput,
  Create,
  SimpleForm,
  Edit,
  email,
  useRecordContext,
  ChipField,
  ArrayField,
  SingleFieldList,
  Toolbar,
  SaveButton,
  DeleteButton,
  Button,
  useNotify,
  EditButton,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  FilterButton,
} from "react-admin";

import MuiButton from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import RotateLeftIcon from "@mui/icons-material/RotateLeft";

import { httpClient } from "../lib/httpClient";
import { bwColours } from "../lib/theme";

const validateEmail = email();

const ScopeBoolean = ({ source }) => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.additionalScopes === undefined) {
    return null;
  }

  return (
    <>
      {record.additionalScopes[source] ? (
        <BooleanField source={`additionalScopes.${source}`} />
      ) : null}
    </>
  );
};

const CustomerChip = () => {
  const rec = useRecordContext();
  if (!rec) return null;

  const color = rec.access === "manager" ? "primary" : "default";
  const sx =
    rec.access === "technician" ? { background: bwColours.secondary.pink } : {};
  return <ChipField source="customerLongName" color={color} sx={sx} />;
};

const userFilters = [
  <TextInput source="email" label="Email" alwaysOn resettable />,
];

export const UserList = () => (
  <List
    pagination={null}
    sort={{ field: "email", order: "ASC" }}
    filters={userFilters}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <DataTable rowClick={false} bulkActionButtons={false}>
      <DataTable.Col source="id" label="ID">
        <TextField source="id" label="ID" />
      </DataTable.Col>
      <DataTable.Col source="email" label="Email">
        <TextField source="email" label="Email" />
      </DataTable.Col>
      <DataTable.Col source="admin" label="Admin" disableSort>
        <ScopeBoolean source="admin" />
      </DataTable.Col>
      <DataTable.Col source="diagnostics" label="Diagnostics" disableSort>
        <ScopeBoolean source="diagnostics" />
      </DataTable.Col>
      <DataTable.Col
        source="customerAssociations"
        label="Customers"
        disableSort
      >
        <ArrayField source="customerAssociations">
          <SingleFieldList>
            <CustomerChip />
          </SingleFieldList>
        </ArrayField>
      </DataTable.Col>
      <DataTable.Col>
        <EditButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const NoHeader = () => null;

const InternalPrivilegeSwitches = () => {
  const record = useRecordContext();
  if (!record) return null;

  const emailAddress = record.email;
  const disabled = emailAddress.endsWith("@barrelwise.ca") ? false : true;
  return (
    <>
      <BooleanInput
        label="Admin"
        source="additionalScopes.admin"
        disabled={disabled}
      />
      <BooleanInput
        label="Diagnostics"
        source="additionalScopes.diagnostics"
        disabled={disabled}
      />
    </>
  );
};

const ReinviteButton = () => {
  const [open, setOpen] = React.useState(false);
  const [running, setRunning] = React.useState(false);

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };
  const handleReboot = () => {
    setRunning(true);
    httpClient("/api/v2/users/" + record.id + "/reinvite", {
      method: "POST",
    })
      .then(async (resp) => {
        const respJson = await resp.json();
        setOpen(false);
        setRunning(false);
        if (resp.ok) {
          notify(record.email + " reinvited", { type: "success" });
        } else {
          notify(respJson?.message || "unknown reinvite error", {
            type: "error",
          });
          console.log(respJson);
        }
      })
      .catch((error) => {
        setOpen(false);
        setRunning(false);
        notify(record.email + " reinvite error", { type: "error" });
        console.log(error);
      });
  };

  return (
    <>
      <Button label="Reinvite" size="medium" onClick={handleOpen}>
        <RotateLeftIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm Reinvite</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reinvite <b>{record.email}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleReboot} disabled={running}>
            Reinvite
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const UserEditToolbar = () => (
  <Toolbar style={{ flex: 1, justifyContent: "space-between" }}>
    <SaveButton label="Save" />
    <ReinviteButton />
    <DeleteButton size="medium" />
  </Toolbar>
);

export const UserEdit = () => (
  <Edit redirect="list">
    <SimpleForm toolbar={<UserEditToolbar />}>
      <NumberInput label="ID" source="id" disabled />
      <TextInput label="Email" source="email" validate={validateEmail} />
      <InternalPrivilegeSwitches />
      <h2>Customer Access</h2>
      <ArrayField source="customerAssociations">
        <DataTable bulkActionButtons={false} head={NoHeader} rowClick={false}>
          <DataTable.Col source="customerLongName" label="Customer">
            <TextField source="customerLongName" label="Customer" />
          </DataTable.Col>
          <DataTable.Col source="access" label="Access">
            <TextField source="access" label="Access" />
          </DataTable.Col>
        </DataTable>
      </ArrayField>
    </SimpleForm>
  </Edit>
);

export const UserCreate = () => (
  <Create redirect="list">
    <SimpleForm>
      <NumberInput label="ID" source="id" disabled />
      <TextInput label="Email" source="email" validate={validateEmail} />
      <p style={{ color: "red", fontWeight: "bold" }}>
        Warning: Creating this user will immediately send them an invite email.
      </p>
    </SimpleForm>
  </Create>
);
